import { Asset, DividendEstimate, DividendEvent, DividendFrequency, SupportedCurrency } from '../types';
import { convertCurrency } from './currency';

export const DIVIDEND_FORECAST_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
export const DIVIDEND_FORECAST_VALIDITY_DAYS = 7;

export interface DividendForecastSyncState {
    lastSyncedAt?: string | null;
    nextAllowedSyncAt?: string | null;
    symbolsHash?: string | null;
    horizonDays?: number | null;
    status?: 'idle' | 'syncing' | 'success' | 'error';
    error?: string | null;
}

export interface BuildDividendForecastOptions {
    holdings: Asset[];
    providerEstimates: Record<string, DividendEstimate[]>;
    aiEstimates?: Record<string, DividendEstimate[]>;
    selectedCurrency: SupportedCurrency;
    horizonDays: number;
    now?: Date;
}

const startOfUtcDay = (date: Date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);

const addMonthsUtc = (date: Date, months: number) => {
    const next = new Date(date);
    next.setUTCMonth(next.getUTCMonth() + months);
    return next;
};

const sameUtcMonth = (left: Date, right: Date) =>
    left.getUTCFullYear() === right.getUTCFullYear() && left.getUTCMonth() === right.getUTCMonth();

export const normalizeDividendFrequency = (frequency?: string | null): DividendFrequency => {
    const value = String(frequency || '').toLowerCase();
    if (value.includes('month')) return 'Monthly';
    if (value.includes('quarter')) return 'Quarterly';
    if (value.includes('semi') || value.includes('bi')) return 'Bi-Annually';
    if (value.includes('annual') || value.includes('year')) return 'Annually';
    return 'Irregular';
};

const monthsStepForFrequency = (frequency: DividendFrequency) => {
    if (frequency === 'Monthly') return 1;
    if (frequency === 'Quarterly') return 3;
    if (frequency === 'Bi-Annually') return 6;
    return 12;
};

const inferFrequencyFromHistory = (events: { paymentDate: string }[]): DividendFrequency => {
    const dates = events
        .map((event) => new Date(`${event.paymentDate.slice(0, 10)}T00:00:00Z`))
        .filter((date) => Number.isFinite(date.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    if (dates.length < 2) return 'Irregular';

    const gaps = dates.slice(1).map((date, index) => (date.getTime() - dates[index].getTime()) / 86400000);
    const avg = gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length;
    if (avg >= 24 && avg <= 45) return 'Monthly';
    if (avg > 45 && avg <= 120) return 'Quarterly';
    if (avg > 120 && avg <= 210) return 'Bi-Annually';
    if (avg > 210 && avg <= 420) return 'Annually';
    return 'Irregular';
};

const chooseEstimateAmount = (events: { amount: number }[]) => {
    const amounts = events.map((event) => Number(event.amount)).filter((amount) => Number.isFinite(amount) && amount > 0);
    if (!amounts.length) return 0;
    const recent = amounts.slice(-4);
    return recent.reduce((sum, amount) => sum + amount, 0) / recent.length;
};

export const buildDividendSymbolsHash = (holdings: Pick<Asset, 'symbol' | 'quantity' | 'type'>[]) =>
    holdings
        .filter((holding) => holding.quantity > 0 && holding.type !== 'Cash')
        .map((holding) => `${holding.symbol.trim().toUpperCase()}:${Number(holding.quantity || 0).toFixed(6)}`)
        .sort()
        .join('|');

export const isDividendForecastCacheFresh = (
    state: DividendForecastSyncState | null | undefined,
    opts: { symbolsHash: string; horizonDays: number; now?: Date } 
) => {
    if (!state?.lastSyncedAt) return false;
    if (state.symbolsHash !== opts.symbolsHash) return false;
    if (Number(state.horizonDays) !== opts.horizonDays) return false;
    const syncedAt = Date.parse(state.lastSyncedAt);
    if (!Number.isFinite(syncedAt)) return false;
    return (opts.now ?? new Date()).getTime() - syncedAt < DIVIDEND_FORECAST_CACHE_TTL_MS;
};

export const buildDividendForecastEvents = ({
    holdings,
    providerEstimates,
    aiEstimates = {},
    selectedCurrency,
    horizonDays,
    now = new Date(),
}: BuildDividendForecastOptions): DividendEvent[] => {
    const today = startOfUtcDay(now);
    const fiveDaysAgo = new Date(today);
    fiveDaysAgo.setUTCDate(fiveDaysAgo.getUTCDate() - 5);
    const horizonDate = new Date(today);
    horizonDate.setUTCDate(horizonDate.getUTCDate() + horizonDays);
    const validUntil = new Date(now.getTime() + DIVIDEND_FORECAST_VALIDITY_DAYS * 86400000).toISOString();
    const generatedAt = now.toISOString();
    const events: DividendEvent[] = [];

    holdings
        .filter((asset) => asset.quantity > 0 && asset.type !== 'Cash')
        .forEach((asset) => {
            const symbol = asset.symbol.toUpperCase();
            const providerForSymbol = [...(providerEstimates[symbol] || [])].sort((a, b) =>
                String(a.payDate || a.exDate || '').localeCompare(String(b.payDate || b.exDate || ''))
            );

            providerForSymbol.forEach((div) => {
                const exDate = div.exDate ?? null;
                const payDate = div.payDate ?? null;
                const eventDate = payDate ?? exDate;
                if (!eventDate) return;
                const uiDate = new Date(`${eventDate.slice(0, 10)}T00:00:00Z`);
                if (uiDate < fiveDaysAgo || uiDate > horizonDate) return;
                const totalAmount = div.amount ?? null;
                const declared = div.source === 'FMP_CALENDAR' || div.source === 'FMP_SYMBOL' || div.source === 'FMP' || div.source === 'Market';
                events.push({
                    id: `${declared ? 'DECL' : 'PROV'}-${symbol}-${eventDate}-${totalAmount ?? 'TBA'}`,
                    symbol,
                    name: asset.name,
                    date: uiDate.toISOString(),
                    eventDate,
                    exDate: exDate ?? undefined,
                    payDate: payDate ?? undefined,
                    amount: totalAmount,
                    cashAmountBase: totalAmount,
                    currency: selectedCurrency,
                    frequency: normalizeDividendFrequency(div.frequency),
                    source: div.source || 'FMP_SYMBOL',
                    provider: div.provider,
                    dateType: payDate ? 'PAYMENT_DATE' : 'EX_DATE',
                    inferredPaymentDate: Boolean(div.inferredPaymentDate),
                    paymentDateInferred: Boolean(div.inferredPaymentDate),
                    declared,
                    confidence: declared ? 0.95 : 0.7,
                    generatedAt,
                    validUntil,
                });
            });

            if (providerForSymbol.length) {
                const seedEvents = providerForSymbol
                    .filter((event) => event.payDate || event.exDate)
                    .map((event) => ({
                        paymentDate: (event.payDate || event.exDate) as string,
                        amount: Number(event.amount || 0),
                        frequency: normalizeDividendFrequency(event.frequency),
                    }))
                    .sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));
                const explicitFrequencies = seedEvents.map((event) => event.frequency).filter((freq) => freq !== 'Irregular');
                const inferredFrequency = explicitFrequencies[explicitFrequencies.length - 1] || inferFrequencyFromHistory(seedEvents);
                const canSafelyEstimate = seedEvents.length >= 3 || (inferredFrequency === 'Monthly' && seedEvents.length >= 1);
                const estimateAmount = chooseEstimateAmount(seedEvents.filter((event) => event.amount > 0));
                const lastKnown = seedEvents[seedEvents.length - 1];

                if (canSafelyEstimate && inferredFrequency !== 'Irregular' && estimateAmount > 0 && lastKnown) {
                    const stepMonths = monthsStepForFrequency(inferredFrequency);
                    let cursor = new Date(`${lastKnown.paymentDate.slice(0, 10)}T00:00:00Z`);
                    while (cursor < today) cursor = addMonthsUtc(cursor, stepMonths);

                    while (cursor <= horizonDate) {
                        const conflicts = seedEvents.some((event) => sameUtcMonth(new Date(`${event.paymentDate.slice(0, 10)}T00:00:00Z`), cursor));
                        if (!conflicts) {
                            const isoDate = toIsoDate(cursor);
                            events.push({
                                id: `EST-${symbol}-${isoDate}`,
                                symbol,
                                name: asset.name,
                                date: cursor.toISOString(),
                                eventDate: isoDate,
                                payDate: isoDate,
                                amount: estimateAmount,
                                cashAmountBase: estimateAmount,
                                currency: selectedCurrency,
                                frequency: inferredFrequency,
                                source: 'ESTIMATED',
                                provider: undefined,
                                dateType: 'PAYMENT_DATE',
                                declared: false,
                                basedOnPaymentDate: lastKnown.paymentDate,
                                confidence: 0.55,
                                generatedAt,
                                validUntil,
                            });
                        }
                        cursor = addMonthsUtc(cursor, stepMonths);
                    }
                }
                return;
            }

            (aiEstimates[symbol] || []).forEach((ai) => {
                const eventDate = ai.payDate ?? ai.exDate;
                if (!eventDate) return;
                const uiDate = new Date(`${eventDate.slice(0, 10)}T00:00:00Z`);
                if (uiDate < fiveDaysAgo || uiDate > horizonDate) return;
                const perShare = Number(ai.amount || 0);
                const totalAmount = perShare > 0 ? convertCurrency(perShare, ai.currency || 'USD', selectedCurrency) * asset.quantity : null;
                events.push({
                    id: `AI-${symbol}-${eventDate}-${perShare || 'TBA'}`,
                    symbol,
                    name: asset.name,
                    date: uiDate.toISOString(),
                    eventDate,
                    exDate: ai.exDate,
                    payDate: ai.payDate,
                    amount: totalAmount,
                    cashAmountBase: totalAmount,
                    currency: selectedCurrency,
                    frequency: normalizeDividendFrequency(ai.frequency),
                    source: 'AI_FORECAST',
                    provider: undefined,
                    dateType: ai.payDate ? 'PAYMENT_DATE' : 'EX_DATE',
                    inferredPaymentDate: Boolean(ai.inferredPaymentDate),
                    paymentDateInferred: Boolean(ai.inferredPaymentDate),
                    declared: false,
                    confidence: 0.45,
                    generatedAt,
                    validUntil,
                });
            });
        });

    const providerSymbols = new Set(events.filter((event) => event.declared).map((event) => event.symbol));
    return events
        .filter((event) => event.source !== 'AI_FORECAST' || !providerSymbols.has(event.symbol))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

export const calculateForwardAnnualDividendFromForecast = (events: DividendEvent[], now = new Date()) => {
    const start = startOfUtcDay(now);
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 365);
    return events.reduce((sum, event) => {
        const date = new Date(event.payDate || event.eventDate || event.date);
        if (!Number.isFinite(date.getTime()) || date < start || date > end) return sum;
        const amount = Number(event.cashAmountBase ?? event.amount ?? 0);
        return Number.isFinite(amount) && amount > 0 ? sum + amount : sum;
    }, 0);
};
